import { useState } from 'react'

import { Box, type IllustrationType } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { RichTextEditor } from '@factorialco/f0-react/dist/experimental'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import {
  NODE_TYPES,
  NodeFormValues,
  getChildTypeText,
  nodeTypeChildren,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

import ChildrenInput from './ChildrenInput'

interface Props {
  onClose: () => void
  description: string
  onSubmit: (formValues: NodeFormValues) => void
  title: string
  icon: IllustrationType
  nodeType: string
  initialValues?: NodeFormValues
  actionType?: 'create' | 'edit'
}

const NodeModal = ({
  onClose,
  title,
  description,
  onSubmit,
  icon,
  nodeType,
  initialValues,
  actionType = 'create',
}: Props) => {
  const [formValues, setFormValues] = useState<NodeFormValues>({
    name: initialValues?.name || '',
    description: initialValues?.description || '',
    children: initialValues?.children || [],
  })
  const { editorConfig } = useRichTextEditor()

  const childType = getChildTypeText(nodeType)
  const childTypePlural = nodeTypeChildren(normalizeNodeType(nodeType))
  const isRoleNode = normalizeNodeType(nodeType) === NODE_TYPES.ROLE

  const handleChange = (field: keyof NodeFormValues, value: string, children?: string[]) => {
    setFormValues((prev) => ({ ...prev, [field]: value, children: children || [] }))
  }

  const disabled = !formValues.name || !formValues.description

  return (
    <Modal
      illustration={icon}
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={() => onSubmit(formValues)}
      footer={
        <Box gap="s16" flexDirection="row" alignItems="center">
          <F0Button
            onClick={onClose}
            label={i18n.t('job_catalog.nodes.modal.cancel')}
            variant="outline"
          />
          <F0Button
            variant="default"
            onClick={() => onSubmit(formValues)}
            label={i18n.t('job_catalog.nodes.modal.submit')}
            disabled={disabled}
          />
        </Box>
      }
    >
      <Box gap="s24">
        <TextField
          label={i18n.t('job_catalog.nodes.modal.field_plain_title')}
          type="text"
          value={formValues.name}
          onChange={(value) => handleChange('name', value)}
        />
        {isRoleNode ? (
          <RichTextEditor
            {...editorConfig}
            title={String(i18n.t('job_catalog.nodes.modal.description'))}
            onChange={(data) => handleChange('description', data?.value ?? '')}
            initialEditorState={{ content: formValues.description }}
            placeholder={String(i18n.t('job_catalog.nodes.modal.description'))}
          />
        ) : (
          <TextArea
            label={i18n.t('job_catalog.nodes.modal.description')}
            value={formValues.description}
            onChange={(value) => handleChange('description', value)}
          />
        )}
        {childType && actionType === 'create' && isRoleNode && (
          <ChildrenInput
            onChange={(children) => handleChange('children', '', children)}
            type={childType}
            typePlural={childTypePlural}
          />
        )}
      </Box>
    </Modal>
  )
}

export default NodeModal
